/**
 * pwa.js — utilitários da PWA do App Técnico (Sprint 6.3).
 *
 * - registra o service worker (só em /installer, só em produção);
 * - detecta a plataforma (Android / iOS / desktop);
 * - captura o evento beforeinstallprompt (Android/Chrome);
 * - expõe o estado "instalado" (standalone).
 *
 * Nada aqui roda quando o app está embrulhado pelo Capacitor —
 * o wrapper nativo já É o app instalado; ver isNativeApp().
 */

let deferredInstallPrompt = null;
const installListeners = new Set();

/** O app está rodando dentro do wrapper nativo (Capacitor)? */
export function isNativeApp() {
  return (
    typeof window !== "undefined" &&
    !!(window.Capacitor && window.Capacitor.isNativePlatform &&
       window.Capacitor.isNativePlatform())
  );
}

/** Detecta a plataforma do aparelho. */
export function detectPlatform() {
  if (typeof navigator === "undefined") return "unknown";
  const ua = navigator.userAgent || "";
  const isIOS =
    /iphone|ipad|ipod/i.test(ua) ||
    // iPad com iPadOS 13+ se identifica como Mac com touch
    (/macintosh/i.test(ua) && navigator.maxTouchPoints > 1);
  if (isIOS) return "ios";
  if (/android/i.test(ua)) return "android";
  return "desktop";
}

/** O app está aberto em modo standalone (instalado)? */
export function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    // iOS Safari usa esta flag não-padrão
    window.navigator.standalone === true ||
    isNativeApp()
  );
}

/**
 * Registra o service worker. Só atua em /installer e fora do wrapper
 * nativo. Retorna a Promise do registro (ou null se não aplicável).
 */
export function registerServiceWorker() {
  if (typeof window === "undefined") return null;
  if (isNativeApp()) return null; // o wrapper nativo não usa SW
  if (!("serviceWorker" in navigator)) return null;
  // só registra para o Portal do Instalador
  if (!window.location.pathname.startsWith("/installer")) return null;

  return navigator.serviceWorker
    .register("/sw.js", { scope: "/installer" })
    .catch((err) => {
      console.warn("[pwa] falha ao registrar service worker:", err);
      return null;
    });
}

/**
 * Captura o beforeinstallprompt (Android/Chrome) para que o app
 * possa oferecer um botão "Instalar" no momento certo.
 */
export function initInstallPrompt() {
  if (typeof window === "undefined") return;

  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    deferredInstallPrompt = e;
    installListeners.forEach((fn) => fn(true));
  });

  window.addEventListener("appinstalled", () => {
    deferredInstallPrompt = null;
    installListeners.forEach((fn) => fn(false));
  });
}

/** Há um prompt de instalação nativo disponível (Android/Chrome)? */
export function canInstall() {
  return deferredInstallPrompt !== null;
}

/**
 * Dispara o prompt de instalação nativo. Resolve com 'accepted',
 * 'dismissed' ou 'unavailable'.
 */
export async function promptInstall() {
  if (!deferredInstallPrompt) return "unavailable";
  deferredInstallPrompt.prompt();
  const choice = await deferredInstallPrompt.userChoice;
  deferredInstallPrompt = null;
  installListeners.forEach((fn) => fn(false));
  return choice && choice.outcome ? choice.outcome : "dismissed";
}

/** Assina mudanças na disponibilidade do prompt. Devolve o unsub. */
export function onInstallAvailabilityChange(fn) {
  installListeners.add(fn);
  return () => installListeners.delete(fn);
}
